import React, { useState, useEffect, useCallback, useRef } from "react";
import maplibregl from "maplibre-gl";
import Supercluster from "supercluster";
import MarkerDetails from "./MarkerDetails.js";
import DatePicker from './DatePicker.js';
import './App.css';
import Preview from './Preview';

// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
//maplibregl.workerClass = require("worker-loader!maplibre-gl/dist/maplibre-gl-csp-worker").default;

function Map({ markers, resultsCount, isMapLoaded, setIsMapLoaded, showPreview, setShowPreview, onSelectedMarkerChange, onMapLoad, flyToMarker }) {

  const [map, setMap] = useState(null);
  //const [mapMarkers, setMapMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  //const [clusterMarkers, setClusterMarkers] = useState([]);
  //const [clickedCluster, setClickedCluster] = useState(null);
  //const [removedClusterId, setRemovedClusterId] = useState(null);
  const [previewMode, setPreviewMode] = useState(true);

  const mapRef = useRef(null);

  /*const setBirdsEyeView = () => {
    if(map) {
      map.setPitch(0); // pitch in degrees
      map.setBearing(0); // bearing in degrees
    }
  };

  const setFrontView = () => {
    if(map) {
      map.setPitch(60); // pitch in degrees
      map.setBearing(0); // bearing in degrees
    }
  };*/

  const incrementPitch = () => {
    if(map) {
      let currentPitch = map.getPitch();
      let newPitch = Math.min(currentPitch + 10, 60);
      map.setPitch(newPitch);
    }
  };
  
  const decrementPitch = () => {
    if(map) {
      let currentPitch = map.getPitch();
      let newPitch = Math.max(currentPitch - 10, 0);
      map.setPitch(newPitch);
    }
  };
  
  const incrementBearing = () => {
    if(map) {
      let currentBearing = map.getBearing();
      let newBearing = (currentBearing + 10) % 360;
      map.setBearing(newBearing);
    }
  };
  
  const decrementBearing = () => {
    if(map) {
      let currentBearing = map.getBearing();
      let newBearing = (currentBearing - 10) % 360;
      map.setBearing(newBearing);
    }
  };
  
/*
  const flyToMarker = useCallback((marker) => {
    if (map) {
      map.flyTo({
        center: [marker.longitude, marker.latitude],
        zoom: 14,
        essential: true,
        offset: [0, -window.innerHeight / 4], // Offset downwards
      });
    }
  }, [map, selectedMarker]);  
  
*/

  useEffect(() => {
    const maplibreApiKey = process.env.REACT_APP_MAPLIBRE_API_KEY;
    console.log('MapLibre API Key:', maplibreApiKey);

    const centerMark = markers[0];

    if (!centerMark){
      return;
    }

    const map = new maplibregl.Map({
      container: "map",
      //style: "https://api.maptiler.com/maps/streets/style.json?key=YrwWR6hQJCPoCsoqvlXY",
      style: `https://api.maptiler.com/maps/streets/style.json?key=${maplibreApiKey}`,
      center: [centerMark.longitude, centerMark.latitude],
      zoom: 10,
      interactive: true,
      pitchWithRotate: true,
    });

    setMap(map);
    mapRef.current = map; //assign the instance ro ref

    if(onMapLoad) {
      onMapLoad(mapRef.current);
    }    

    map.on('error', (error) => {
      console.error('Map error:', error);
    });
  
    const nav = new maplibregl.NavigationControl();
    map.addControl(nav, "top-right");
  
    map.on("load", () => {
      console.log("map is loaded");


      map.addSource("terrain", {
        "type": "raster-dem",
        "url": `https://api.maptiler.com/tiles/terrain-rgb/tiles.json?key=${maplibreApiKey}`,
      });
      map.setTerrain({
        source: "terrain",
        exaggeration: 5
      });

      map.addSource("markers", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: markers.map((marker) => {
            // Add a small random offset to the coordinates
            const offset = 0.0001;
            const randomOffsetLng = (Math.random() - 0.5) * 2 * offset;
            const randomOffsetLat = (Math.random() - 0.5) * 2 * offset;
    
            return {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [
                  marker.longitude + randomOffsetLng,
                  marker.latitude + randomOffsetLat,
                ],
              },
              properties: { ...marker },
            };
          }),
        },
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 20,
      });
  
      map.addLayer({
        id: "clusters",
        type: "circle",
        source: "markers",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": [
            "step",
            ["get", "point_count"],
            "#51bbd6",
            100,
            "#f1f075",
            750,
            "#f28cb1",
          ],
          "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
        },
      });
  
      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "markers",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12,
        },
      });
  
      map.addLayer({
        id: "unclustered-point",
        type: "circle",
        source: "markers",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": "#FF0000",
          "circle-radius": 10,
          "circle-stroke-width": 1,
          "circle-stroke-color": "#fff",
        },
      });
  
      map.on("click", "clusters", (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ["clusters"],
        });
        const clusterId = features[0].properties.cluster_id;
        map.getSource("markers").getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
      
          const currentZoom = map.getZoom();
          const zoomIncrement = 2; // You can adjust this value as needed
          const targetZoom = Math.min(zoom, currentZoom + zoomIncrement);
      
          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: targetZoom,
          });
        });
      });      
  
      map.on("click", "unclustered-point", (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        let markerProps = e.features[0].properties;
    
        // Check if hashtags are stringified and parse them
        if (typeof markerProps.hashtag === 'string') {
            try {
                markerProps.hashtag = JSON.parse(markerProps.hashtag);
            } catch (error) {
                console.error('Failed to parse hashtags:', error);
            }
        }
        
        setSelectedMarker(markerProps);
        onSelectedMarkerChange(markerProps);
      
        const currentZoom = map.getZoom();
        const targetZoom = Math.max(14, currentZoom);
      
        map.easeTo({
            center: coordinates,
            zoom: targetZoom,
        });
    });    
      
  
      fitMapToMarkers(map, markers);

      ["clusters", "unclustered-point"].forEach((layer) => {
        map.on("mouseenter", layer, () => {
          map.getCanvas().style.cursor = "pointer";
        });
      });
    
      // Add mouseenter event listener for clusters and unclustered-point layers
      ["clusters", "unclustered-point"].forEach((layer) => {
        map.on("mouseenter", layer, () => {
          map.getCanvas().style.cursor = "pointer";
          if (layer === "clusters") {
            map.setPaintProperty("clusters", "circle-opacity", 0.7);
          }
        });
      });

      // Add mouseleave event listener for clusters and unclustered-point layers
      ["clusters", "unclustered-point"].forEach((layer) => {
        map.on("mouseleave", layer, () => {
          map.getCanvas().style.cursor = "";
          if (layer === "clusters") {
            map.setPaintProperty("clusters", "circle-opacity", 1);
          }
        });
      });
      setIsMapLoaded(true);
    });
  
    return () => {
      map.remove();
    };
  }, [markers]);
  
  

  useEffect(() => {
    if (selectedMarker && map) {
      if (markers.some(marker => marker.id === selectedMarker.id)) {
        flyToMarker(selectedMarker);
      }
    }
  }, [selectedMarker, map, markers]);    

  const fitMapToMarkers = (map, markers) => {
    const bounds = new maplibregl.LngLatBounds();
  
    markers.forEach(marker => {
      if (isNaN(marker.longitude) || isNaN(marker.latitude)) {
        console.error(`Invalid marker: ${JSON.stringify(marker)}`);
      } else {
          bounds.extend([marker.longitude, marker.latitude]);
      }
    });
  
    map.fitBounds(bounds, { padding: 250 });
  };
  
  useEffect(() => {
    const handleKeyDown = (event) => {
      const moveDistance = 100; // adjust this value as needed
      const rotateAngle = 10; // adjust this value as needed
  
      switch (event.key) {
        case 'ArrowUp':
          incrementPitch();
          break;
        case 'ArrowDown':
          decrementPitch();
          break;
        case 'ArrowRight':
          incrementBearing();
          break;
        case 'ArrowLeft':
          decrementBearing();
          break;
        case 'w':
          if (map) {
            map.panBy([0, -moveDistance]);
          }
          break;
        case 's':
          if (map) {
            map.panBy([0, moveDistance]);
          }
          break;
        case 'a':
          if (map) {
            map.panBy([-moveDistance, 0]);
          }
          break;
        case 'd':
          if (map) {
            map.panBy([moveDistance, 0]);
          }
          break;
        case 'q':
          if (map) {
            map.rotateTo(map.getBearing() - rotateAngle, { duration: 0 });
          }
          break;
        case 'e':
          if (map) {
            map.rotateTo(map.getBearing() + rotateAngle, { duration: 0 });
          }
          break;
        default:
          break;
      }
    };
  
    window.addEventListener("keydown", handleKeyDown);
  
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [incrementPitch, decrementPitch, incrementBearing, decrementBearing, map]);
  

  return (
    <>
      <div id="map" style={{ height: "100vh", width: "100%" }} />
      
      
      
    </>
  );
  
}

export default Map;
